import React, { useEffect, useState } from 'react';
import { Accessibility, Type, Sun, X, Check } from 'lucide-react';

const STORAGE_KEY = 'a11y_prefs_v1';

const defaultPrefs = { fontScale: 1, highContrast: false, openOnLoad: false };

const loadPrefs = () => {
  try { return { ...defaultPrefs, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') }; }
  catch { return defaultPrefs; }
};

const applyPrefs = (p) => {
  document.documentElement.style.fontSize = `${(p.fontScale || 1) * 100}%`;
  if (p.highContrast) document.body.classList.add('a11y-high-contrast');
  else document.body.classList.remove('a11y-high-contrast');
};

export const AccessibilityButton = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => { applyPrefs(prefs); localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); }, [prefs]);

  const setScale = (s) => setPrefs(p => ({ ...p, fontScale: s }));
  const toggleContrast = () => setPrefs(p => ({ ...p, highContrast: !p.highContrast }));
  const reset = () => setPrefs(defaultPrefs);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        title="Bezbariérové ovládání"
        aria-label="Otevřít nastavení přístupnosti"
        className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
        data-testid="a11y-toggle"
      >
        <Accessibility size={16} />
        <span className="hidden md:inline text-xs">Přístupnost</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 bg-white text-[#2D3748] rounded-2xl shadow-xl border border-slate-200 p-4 z-50" data-testid="a11y-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-base flex items-center gap-2"><Accessibility size={18} /> Přístupnost</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Zavřít">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium mb-2 flex items-center gap-2"><Type size={14} /> Velikost písma</div>
                <div className="grid grid-cols-3 gap-1">
                  {[{ s: 1, l: 'Normální', tid: 'a11y-font-normal' }, { s: 1.2, l: 'Větší', tid: 'a11y-font-large' }, { s: 1.4, l: 'Velké', tid: 'a11y-font-xlarge' }].map(o => (
                    <button
                      key={o.s}
                      onClick={() => setScale(o.s)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${prefs.fontScale === o.s ? 'bg-[#4ECDC4] text-white border-[#4ECDC4]' : 'bg-white border-slate-200 hover:border-[#4ECDC4]'}`}
                      data-testid={o.tid}
                    >
                      {prefs.fontScale === o.s && <Check size={12} className="inline mr-1" />}{o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <button
                  onClick={toggleContrast}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center justify-between ${prefs.highContrast ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-white border-slate-200 hover:border-amber-300'}`}
                  data-testid="a11y-contrast-toggle"
                >
                  <span className="flex items-center gap-2"><Sun size={14} /> Vysoký kontrast</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${prefs.highContrast ? 'bg-amber-300 text-amber-900' : 'bg-slate-100 text-slate-500'}`}>
                    {prefs.highContrast ? 'Zapnuto' : 'Vypnuto'}
                  </span>
                </button>
              </div>

              <button onClick={reset} className="w-full text-xs text-slate-500 hover:text-slate-700 underline pt-1" data-testid="a11y-reset">
                Obnovit výchozí nastavení
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessibilityButton;
